(() => {
  const script = document.currentScript;
  const storeCode = script?.dataset?.pricelabStore || "";
  const endpoint = script?.dataset?.pricelabEndpoint || "";
  const storefrontEpoch = script?.dataset?.pricelabStorefrontEpoch || "";
  if (!storeCode || !endpoint.startsWith("https://")) return;

  const anonymousKey = "crystal_pricelab_visitor_v1";
  const assignmentKey = "crystal_pricelab_assignments_v1";
  const propertyName = "properties[_pricelab_v1]";
  const productFormSelector =
    'main product-form form[action*="/cart/add"]';
  const priceSurfaceSelector = "[data-pricelab-price-surface]";
  const pendingAssignments = new Map();
  const pageAssignments = new Map();
  const observedPriceSurfaces = new WeakSet();
  const validationTtlMs = 9000;

  const randomId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto?.getRandomValues?.(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  };

  const anonymousId = () => {
    let value = localStorage.getItem(anonymousKey);
    if (!value || value.length < 16) {
      value = randomId();
      localStorage.setItem(anonymousKey, value);
    }
    return value;
  };

  const readState = () => {
    try {
      const state = JSON.parse(localStorage.getItem(assignmentKey) || "{}");
      return {
        version: 1,
        assignments: state.assignments || {},
      };
    } catch (_) {
      return {version: 1, assignments: {}};
    }
  };

  const writeAssignment = (assignment) => {
    const state = readState();
    Object.entries(state.assignments).forEach(([key, value]) => {
      if (
        !value?.expiresAt ||
        Date.parse(value.expiresAt) <= Date.now() ||
        value.storefrontEpoch !== storefrontEpoch
      ) {
        delete state.assignments[key];
      }
    });
    if (assignment?.active && assignment.experimentId) {
      const variantId = String(assignment.variantId || "").split("/").pop();
      state.assignments[`${assignment.experimentId}:${variantId}`] = {
        ...assignment,
        storefrontEpoch,
      };
    }
    localStorage.setItem(assignmentKey, JSON.stringify(state));
  };

  const purgeStoredAssignments = (variantId) => {
    const normalized = String(variantId || "").split("/").pop();
    const state = readState();
    Object.entries(state.assignments).forEach(([key, value]) => {
      if (String(value?.variantId || "") === normalized) delete state.assignments[key];
    });
    localStorage.setItem(assignmentKey, JSON.stringify(state));
  };

  const validatedAssignment = (variantId) => {
    const normalized = String(variantId || "").split("/").pop();
    const validated = pageAssignments.get(normalized);
    if (!validated || Date.now() - validated.validatedAt > validationTtlMs) {
      return {known: false, assignment: null};
    }
    return {known: true, assignment: validated.assignment};
  };

  const activeAssignment = (variantId) => validatedAssignment(variantId).assignment;

  const restorePriceSurface = (surface) => {
    surface.querySelectorAll("[data-pricelab-surface-original-text]").forEach((node) => {
      node.textContent = node.dataset.pricelabSurfaceOriginalText;
      delete node.dataset.pricelabSurfaceOriginalText;
    });
    if (surface.dataset.pricelabSurfaceOriginalText) {
      surface.textContent = surface.dataset.pricelabSurfaceOriginalText;
      delete surface.dataset.pricelabSurfaceOriginalText;
    }
    delete surface.dataset.pricelabSurfaceRenderKey;
  };

  const renderPriceSurface = (surface, assignment) => {
    const renderKey = assignment?.active && assignment.changesPrice
      ? [assignment.assignmentId, assignment.variantId, assignment.assignedPrice].join(":")
      : "";
    if (renderKey && surface.dataset.pricelabSurfaceRenderKey === renderKey) return true;
    if (!assignment?.active || !assignment.changesPrice) {
      restorePriceSurface(surface);
      return true;
    }

    restorePriceSurface(surface);

    const prefix = surface.dataset.pricelabPrefix || "";
    const formatted = `${prefix}${money(assignment.assignedPrice, surface.dataset.moneyFormat)}`;
    const nodes = [...surface.querySelectorAll(
      ".price__regular .price-item--regular, .price__sale .price-item--sale",
    )].filter((node) => node.offsetParent !== null);
    if (nodes.length) {
      nodes.forEach((node) => {
        node.dataset.pricelabSurfaceOriginalText = node.textContent;
        node.textContent = formatted;
      });
    } else {
      surface.dataset.pricelabSurfaceOriginalText = surface.textContent;
      surface.textContent = formatted;
    }
    surface.dataset.pricelabSurfaceRenderKey = renderKey;
    return true;
  };

  const syncFormProperty = (form, assignment) => {
    let input = form.querySelector(`[name="${propertyName}"]`);
    if (!assignment) {
      input?.remove();
      return;
    }
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = propertyName;
      input.dataset.pricelabAssignment = "true";
      form.append(input);
    }
    input.value = assignment.token;
  };

  const money = (amount, format) => {
    if (typeof window.formatMoneyCustomTemplate === "function" && format) {
      return window.formatMoneyCustomTemplate(Number(amount), format);
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(amount));
  };

  const recalculateDerivedPrices = () => {
    if (
      typeof PUB_SUB_EVENTS !== "undefined" &&
      PUB_SUB_EVENTS.productFormUpdated &&
      typeof publish === "function"
    ) {
      publish(PUB_SUB_EVENTS.productFormUpdated, {data: {source: "pricelab"}});
    }
  };

  const restoreRenderedPrices = (root) => {
    let restored = false;
    root.querySelectorAll("[data-pricelab-original-price]").forEach((container) => {
      restored = true;
      container.dataset.price = container.dataset.pricelabOriginalPrice;
      delete container.dataset.pricelabOriginalPrice;
      delete container.dataset.pricelabRenderKey;
      container.querySelectorAll("[data-pricelab-original-text]").forEach((node) => {
        node.textContent = node.dataset.pricelabOriginalText;
        delete node.dataset.pricelabOriginalText;
      });
    });
    if (restored) recalculateDerivedPrices();
  };

  const renderAssignedPrice = (form, assignment) => {
    const root = form.closest("product-info") || form.closest("main") || document;
    if (!assignment?.active || !assignment.changesPrice) {
      restoreRenderedPrices(root);
      document.dispatchEvent(new CustomEvent("pricelab:price-rendered", {
        detail: {assignment, rendered: true},
      }));
      return true;
    }

    const canonicalCents = String(Math.round(Number(assignment.canonicalPrice) * 100));
    const assignedCents = String(Math.round(Number(assignment.assignedPrice) * 100));
    const renderKey = [
      assignment.assignmentId,
      assignment.variantId,
      assignedCents,
    ].join(":");
    const selectedSize = root.querySelector(
      'variant-selects .size-variant:has(input:checked) .price__container',
    );
    const summarySizes = [...root.querySelectorAll(
      '.product-form ul.result-list li[data-option-name] .price__container',
    )];
    const headlinePrice = [...root.querySelectorAll(".price__container")].find(
      (container) =>
        (
          container.dataset.price === canonicalCents ||
          container.dataset.pricelabRenderKey === renderKey
        ) &&
        !container.closest("variant-selects") &&
        !container.closest(".result-list") &&
        !container.closest(".product-additional"),
    );
    const targets = [...new Set([headlinePrice, selectedSize, ...summarySizes].filter(Boolean))];
    if (
      targets.length === 4 &&
      targets.every(
        (target) =>
          target.dataset.pricelabRenderKey === renderKey &&
          target.dataset.price === assignedCents,
      )
    ) {
      return true;
    }
    restoreRenderedPrices(root);
    if (targets.length !== 4 || targets.some((target) => target.dataset.price !== canonicalCents)) {
      restoreRenderedPrices(root);
      return false;
    }
    targets.forEach((container) => {
      container.dataset.pricelabOriginalPrice = container.dataset.price;
      container.dataset.pricelabRenderKey = renderKey;
      container.dataset.price = assignedCents;
      const visiblePrices = container.querySelectorAll(
        ".price__regular .price-item--regular, .price__sale .price-item--sale",
      );
      visiblePrices.forEach((node) => {
        node.dataset.pricelabOriginalText = node.textContent;
        node.textContent = money(assignment.assignedPrice, container.dataset.moneyFormat);
      });
    });
    recalculateDerivedPrices();
    document.dispatchEvent(new CustomEvent("pricelab:price-rendered", {
      detail: {assignment, rendered: true},
    }));
    return true;
  };

  const assignmentRendered = (form, assignment) => {
    if (!assignment?.active) return false;
    if (!assignment.changesPrice) return true;
    const root = form.closest("product-info") || form.closest("main") || document;
    const assignedCents = String(Math.round(Number(assignment.assignedPrice) * 100));
    const renderKey = [assignment.assignmentId, assignment.variantId, assignedCents].join(":");
    const marked = [...root.querySelectorAll(`[data-pricelab-render-key="${renderKey}"]`)];
    return marked.length === 4 && marked.every((node) => node.dataset.price === assignedCents);
  };

  const publishExposure = (assignment) => {
    const exposureKey = [
      "crystal_pricelab_exposure_v1",
      assignment.assignmentId,
      assignment.variantId,
    ].join(":");
    if (sessionStorage.getItem(exposureKey)) return;
    if (!window.Shopify?.analytics?.publish) return;
    sessionStorage.setItem(exposureKey, "1");
    window.Shopify.analytics.publish("crystal_pricelab_exposure", {
      assignmentId: assignment.assignmentId,
      experimentKey: assignment.experimentKey,
      armKey: assignment.armKey,
      variantId: assignment.variantId,
      displayedPrice: assignment.assignedPrice,
      storefrontEpoch,
    });
  };

  const fetchAssignment = async (variantId) => {
    const normalized = String(variantId || "").split("/").pop();
    if (!normalized) return null;
    const validated = validatedAssignment(normalized);
    if (validated.known) return validated.assignment;
    const pending = pendingAssignments.get(normalized);
    if (pending) return pending;
    const request = (async () => {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            variantId: normalized,
            anonymousId: anonymousId(),
          }),
        });
        if (!response.ok) throw new Error("Assignment validation failed");
        const assignment = await response.json();
        if (!assignment.active) {
          pageAssignments.set(normalized, {assignment: null, validatedAt: Date.now()});
          purgeStoredAssignments(normalized);
          return null;
        }
        writeAssignment(assignment);
        pageAssignments.set(normalized, {assignment, validatedAt: Date.now()});
        return assignment;
      } catch (_) {
        pageAssignments.delete(normalized);
        return null;
      }
    })();
    pendingAssignments.set(normalized, request);
    try {
      return await request;
    } finally {
      if (pendingAssignments.get(normalized) === request) {
        pendingAssignments.delete(normalized);
      }
    }
  };

  const syncForm = async (form) => {
    const variantInput = form.querySelector('[name="id"]');
    const variantId = variantInput?.value;
    if (!variantId) return;
    const assignment = await fetchAssignment(variantId);
    if (String(variantInput.value) !== String(variantId)) return;
    const rendered = renderAssignedPrice(form, assignment);
    syncFormProperty(form, rendered ? assignment : null);
    if (assignment && rendered) publishExposure(assignment);
  };

  const syncAllForms = (root = document) => {
    const forms = [];
    if (
      root instanceof HTMLFormElement &&
      root.matches(productFormSelector)
    ) {
      forms.push(root);
    }
    root
      .querySelectorAll?.(productFormSelector)
      .forEach((form) => forms.push(form));
    [...new Set(forms)].forEach((form) => syncForm(form));
  };

  const syncPriceSurface = async (surface) => {
    const variantId = surface.dataset.pricelabVariantId;
    if (!variantId) return;
    const assignment = await fetchAssignment(variantId);
    if (String(surface.dataset.pricelabVariantId) !== String(variantId)) return;
    const matchingForm = [...document.querySelectorAll(productFormSelector)].find(
      (form) => String(form.querySelector('[name="id"]')?.value) === String(variantId),
    );
    if (
      matchingForm &&
      assignment?.changesPrice &&
      !assignmentRendered(matchingForm, assignment)
    ) {
      restorePriceSurface(surface);
      return;
    }
    renderPriceSurface(surface, assignment);
    if (assignment && surface.offsetParent !== null) publishExposure(assignment);
  };

  const priceSurfaceObserver = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) syncPriceSurface(entry.target);
      });
    }, {rootMargin: "200px"})
    : null;

  const observePriceSurface = (surface) => {
    if (observedPriceSurfaces.has(surface)) return;
    observedPriceSurfaces.add(surface);
    if (priceSurfaceObserver) {
      priceSurfaceObserver.observe(surface);
    } else if (surface.offsetParent !== null) {
      syncPriceSurface(surface);
    }
  };

  const syncAllPriceSurfaces = (root = document) => {
    const surfaces = [];
    if (root instanceof HTMLElement && root.matches(priceSurfaceSelector)) surfaces.push(root);
    root.querySelectorAll?.(priceSurfaceSelector).forEach((surface) => surfaces.push(surface));
    [...new Set(surfaces)].forEach((surface) => observePriceSurface(surface));
  };

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const variantId = form.querySelector('[name="id"]')?.value;
      const assignment = activeAssignment(variantId);
      syncFormProperty(
        form,
        assignmentRendered(form, assignment) ? assignment : null,
      );
    },
    true,
  );

  document.addEventListener("change", (event) => {
    if (event.target?.matches?.('[name="id"], [data-option-value-id]')) {
      const form = event.target.closest("form") || document;
      setTimeout(() => syncAllForms(form), 0);
    }
  });

  new MutationObserver((mutations) => {
    if (
      mutations.some(
        (mutation) =>
          mutation.type === "childList" ||
          mutation.target?.matches?.('[name="id"]'),
      )
    ) {
      syncAllForms();
      syncAllPriceSurfaces();
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["value"],
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      syncAllForms();
      syncAllPriceSurfaces();
    }, {once: true});
  } else {
    syncAllForms();
    syncAllPriceSurfaces();
  }
  setInterval(() => {
    syncAllForms();
    document
      .querySelectorAll(`${priceSurfaceSelector}[data-pricelab-surface-render-key]`)
      .forEach((surface) => syncPriceSurface(surface));
  }, 5000);
})();
